<template>
    <div class="pitch-recorder-widget">
        <v-chip
            :color="isRecording ? 'error' : 'secondary'"
            :variant="isRecording ? 'elevated' : 'outlined'"
            size="small"
            class="mr-2"
        >
            <v-icon size="small" :class="{ 'recording-pulse': isRecording }">
                {{ isRecording ? 'mdi-record-circle' : 'mdi-circle-outline' }}
            </v-icon>
            <span class="ml-1">{{ frameCount }} frames</span>
        </v-chip>

        <v-btn
            v-if="!isRecording"
            color="error"
            size="small"
            variant="elevated"
            @click="startRecording"
        >
            <v-icon size="small">mdi-record</v-icon>
            <span class="ml-1">Record</span>
        </v-btn>

        <v-btn
            v-else
            color="secondary"
            size="small"
            variant="elevated"
            @click="stopRecording"
        >
            <v-icon size="small">mdi-stop</v-icon>
            <span class="ml-1">Stop</span>
        </v-btn>

        <v-btn
            v-if="frameCount > 0 && !isRecording"
            color="primary"
            size="small"
            variant="elevated"
            class="ml-2"
            @click="downloadRecording"
        >
            <v-icon size="small">mdi-download</v-icon>
            <span class="ml-1">Download</span>
        </v-btn>

        <v-btn
            v-if="frameCount > 0 && !isRecording"
            color="grey"
            size="small"
            variant="text"
            class="ml-1"
            @click="clearRecording"
        >
            <v-icon size="small">mdi-delete</v-icon>
        </v-btn>

        <!-- Recording Dialog -->
        <v-dialog v-model="showDialog" max-width="500">
            <v-card>
                <v-card-title>Start Recording</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="description"
                        label="Description"
                        hint="E.g., 'faint-whistle-dropouts' or 'fast-scale-run'"
                        persistent-hint
                        autofocus
                    />

                    <v-select
                        v-model="expectedOutcome"
                        label="Expected Outcome"
                        :items="['success', 'failure', 'contested']"
                        hint="What should happen when this recording is replayed?"
                        persistent-hint
                        class="mt-4"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey" @click="showDialog = false">Cancel</v-btn>
                    <v-btn color="error" @click="confirmStart">
                        <v-icon size="small">mdi-record</v-icon>
                        <span class="ml-1">Start Recording</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { usePitchRecorder } from '../composables/usePitchRecorder';
import type { PitchDetectionResult } from '../index';

interface Props {
    exerciseType: string;
    exerciseConfig: any;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'recording-started': [];
    'recording-stopped': [];
    'frame-captured': [frame: PitchDetectionResult];
}>();

// Recorder state
const recorder = usePitchRecorder();

// Dialog state
const showDialog = ref(false);
const description = ref('');
const expectedOutcome = ref<'success' | 'failure' | 'contested'>('success');

// Computed
const frameCount = computed(() => recorder.frames.value.length);
const isRecording = computed(() => recorder.isRecording.value);

// Watch for recording state to emit events
watch(isRecording, (newValue, oldValue) => {
    if (newValue && !oldValue) {
        emit('recording-started');
    } else if (!newValue && oldValue) {
        emit('recording-stopped');
    }
});

// Methods
function startRecording() {
    showDialog.value = true;
}

function confirmStart() {
    recorder.start({
        exerciseType: props.exerciseType,
        exerciseConfig: props.exerciseConfig,
        description: description.value || undefined,
        expectedOutcome: expectedOutcome.value,
    });

    showDialog.value = false;

    // Reset for next time
    description.value = '';
    expectedOutcome.value = 'success';
}

function stopRecording() {
    recorder.stop();
}

function downloadRecording() {
    const filename = description.value
        ? `${props.exerciseType}-${description.value}.json`
        : undefined;

    recorder.download(filename);
}

function clearRecording() {
    if (confirm('Clear this recording without downloading?')) {
        recorder.clear();
    }
}

/**
 * Capture a pitch detection frame
 * Call this from parent component in the pitch detection loop
 */
function capture(result: PitchDetectionResult) {
    if (recorder.isRecording.value) {
        recorder.capture(result);
        emit('frame-captured', result);
    }
}

// Expose capture method to parent
defineExpose({
    capture,
    isRecording,
    frameCount,
});
</script>

<style scoped>
.pitch-recorder-widget {
    display: flex;
    align-items: center;
    gap: 8px;
}

.recording-pulse {
    animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}
</style>
